/**
 * DocumentsList Component
 *
 * Displays a grid or list of documents for a CaseFile.
 * Fetches data from listDocuments Firebase callable.
 */

import { useMemo, useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { DocumentCard, DocumentSummary } from './DocumentCard';
import { FileText, Grid, List, Search, Sparkles } from 'lucide-react';
import { searchDocuments } from '../../utils/documentSearch';
import { useDebounce } from '../../hooks/useDebounce';
import { useSemanticSearch } from '../../hooks/useSemanticSearch';

interface ListDocumentsResponse {
  success: boolean;
  documents: DocumentSummary[];
  totalCount: number;
}

interface DocumentsListProps {
  caseFileId: string;
  viewMode?: 'grid' | 'list';
  categoryFilter?: string; // UI category label to filter by (e.g., "Medical Records")
  onDocumentClick?: (documentId: string) => void;
  onDocumentsLoaded?: (documents: DocumentSummary[]) => void; // Callback when documents are loaded
}

export function DocumentsList({
  caseFileId,
  viewMode: initialViewMode = 'grid',
  categoryFilter,
  onDocumentClick,
  onDocumentsLoaded,
}: DocumentsListProps): JSX.Element {
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(initialViewMode);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'uploadedAt' | 'fileName'>('uploadedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);

    try {
      const listDocuments = httpsCallable<
        { caseFileId: string; orderBy?: string; orderDirection?: string },
        ListDocumentsResponse
      >(functions, 'listDocuments');

      const result = await listDocuments({
        caseFileId,
        orderBy: sortBy,
        orderDirection: sortDirection,
      });

      if (result.data.success) {
        setDocuments(result.data.documents);
        // Notify parent of all documents for category counting
        onDocumentsLoaded?.(result.data.documents);
      } else {
        setError('Failed to load documents');
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [caseFileId, sortBy, sortDirection]);

  const handleDownload = async (documentId: string) => {
    try {
      const getDocument = httpsCallable<
        { caseFileId: string; documentId: string },
        { success: boolean; downloadUrl: string }
      >(functions, 'getDocument');

      const result = await getDocument({ caseFileId, documentId });

      if (result.data.success && result.data.downloadUrl) {
        window.open(result.data.downloadUrl, '_blank');
      }
    } catch (err) {
      console.error('Error downloading document:', err);
    }
  };

  const handleDelete = async (documentId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const deleteDocument = httpsCallable<
        { caseFileId: string; documentId: string },
        { success: boolean }
      >(functions, 'deleteDocument');

      const result = await deleteDocument({ caseFileId, documentId });

      if (result.data.success) {
        setDocuments((prev) => prev.filter((d) => d.documentId !== documentId));
      }
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const handleDocumentClick = (documentId: string) => {
    if (onDocumentClick) {
      onDocumentClick(documentId);
    } else {
      // Open document via signed URL (no dedicated viewer route exists)
      handleDownload(documentId);
    }
  };

  // Apply category filter first, then debounced multi-field search across the
  // AI-extracted content surfaced by listDocuments (searchableText fast path).
  const debouncedSearchTerm = useDebounce(searchTerm, 200);
  const categoryFiltered = documents.filter(
    (doc) => !categoryFilter || categoryFilter === 'all' || doc.category === categoryFilter
  );
  const phaseAFiltered = searchDocuments(categoryFiltered, debouncedSearchTerm);

  // TASK-022: semantic search scoped to this CaseFile. Fires in parallel with
  // Phase A keyword filtering above. The hook is feature-flagged
  // (VITE_FEATURE_SEMANTIC_SEARCH); when off, this is a no-op.
  const semantic = useSemanticSearch({
    query: searchTerm,
    caseFileId,
    limit: 50,
  });

  // Merge Phase A (keyword) and Phase B (semantic) — Phase B docs first with
  // snippets, then Phase A matches not already in Phase B as "Also matching".
  // Category filter applies to both so a user who's filtered to a category
  // doesn't see semantic results from other categories.
  const filteredDocuments = useMemo(() => {
    const phaseAById = new Map<string, DocumentSummary>();
    for (const d of phaseAFiltered) phaseAById.set(d.documentId, d);

    // Documents in this CaseFile, indexed by id, for resolving Phase B hits
    // back to the full DocumentSummary shape (Phase B only returns a subset of
    // the fields the card needs — fileType, fileSizeBytes, etc.).
    const docsById = new Map<string, DocumentSummary>();
    for (const d of categoryFiltered) docsById.set(d.documentId, d);

    type Merged = DocumentSummary & {
      _snippets?: string[];
      _source: 'phaseA' | 'phaseB';
    };
    const merged: Merged[] = [];
    const phaseBSeen = new Set<string>();

    if (semantic.enabled && semantic.status === 'success' && debouncedSearchTerm.trim()) {
      for (const r of semantic.results) {
        // Phase B may return docs the user has access to but that aren't part
        // of THIS CaseFile's loaded set — skip those (they'll surface in global
        // search instead). Also respect the active category filter.
        const localDoc = docsById.get(r.documentId);
        if (!localDoc) continue;
        phaseBSeen.add(r.documentId);
        merged.push({
          ...localDoc,
          _snippets: r.snippets,
          _source: 'phaseB',
        });
      }
    }

    for (const d of phaseAFiltered) {
      if (phaseBSeen.has(d.documentId)) continue;
      merged.push({ ...d, _source: 'phaseA' });
    }

    return merged;
  }, [
    phaseAFiltered,
    categoryFiltered,
    semantic.enabled,
    semantic.status,
    semantic.results,
    debouncedSearchTerm,
  ]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="h-8 w-32 bg-soft-neutral rounded animate-pulse" />
          <div className="h-8 w-24 bg-soft-neutral rounded animate-pulse" />
        </div>
        <div className={`grid gap-4 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : ''}`}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="card">
              <div className="card-body">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-soft-neutral rounded-lg animate-pulse" />
                  <div className="flex-1">
                    <div className="h-4 w-32 bg-soft-neutral rounded animate-pulse mb-2" />
                    <div className="h-3 w-20 bg-soft-neutral rounded animate-pulse" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error flex justify-between items-center">
        <span>Error: {error}</span>
        <button onClick={fetchDocuments} className="btn-ghost btn-sm">
          Retry
        </button>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="card">
        <div className="card-body text-center py-12">
          <FileText size={48} className="mx-auto text-muted mb-4" />
          <h3 className="text-lg font-semibold text-ink mb-2">No documents yet</h3>
          <p className="text-muted mb-4">
            Upload documents like IEPs, medical records, or insurance forms.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-9"
          />
          {semantic.enabled && semantic.status === 'loading' && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-xs text-muted">
              <Sparkles size={12} className="animate-pulse" />
              searching deeper&hellip;
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <select
            value={`${sortBy}-${sortDirection}`}
            onChange={(e) => {
              const [by, dir] = e.target.value.split('-');
              setSortBy(by as 'uploadedAt' | 'fileName');
              setSortDirection(dir as 'asc' | 'desc');
            }}
            className="input w-auto"
          >
            <option value="uploadedAt-desc">Newest first</option>
            <option value="uploadedAt-asc">Oldest first</option>
            <option value="fileName-asc">Name A-Z</option>
            <option value="fileName-desc">Name Z-A</option>
          </select>
          <div className="flex border border-soft-neutral rounded-md">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-soft-neutral' : ''}`}
              aria-label="Grid view"
              aria-pressed={viewMode === 'grid'}
            >
              <Grid size={18} className="text-muted" aria-hidden="true" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-soft-neutral' : ''}`}
              aria-label="List view"
              aria-pressed={viewMode === 'list'}
            >
              <List size={18} className="text-muted" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Documents grid/list */}
      <div className={viewMode === 'grid'
        ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
        : 'space-y-3'
      }>
        {filteredDocuments.map((doc) => (
          <DocumentCard
            key={doc.documentId}
            document={doc}
            onClick={() => handleDocumentClick(doc.documentId)}
            onDownload={handleDownload}
            onDelete={handleDelete}
            snippets={doc._snippets}
            matchSource={doc._source}
          />
        ))}
      </div>

      {filteredDocuments.length === 0 && debouncedSearchTerm && (
        <div className="text-center py-8 text-muted">
          No documents match "{debouncedSearchTerm}"
        </div>
      )}
    </div>
  );
}

export default DocumentsList;
